import { useCallback, useEffect, useState } from 'react';

import PubSub from 'pubsub-js';
import {
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import { FileUploadPubSubPayload } from '@components/FileBrowser/utils/fileUpload/types';

interface IUploadCardProps {
  id: string;
  location: string;
}

const getStatus = (error: boolean, complete: boolean) => {
  if (error) {
    return { label: 'Needs attention', color: 'error' as const };
  }

  if (complete) {
    return { label: 'Complete', color: 'success' as const };
  }

  return { label: 'Uploading', color: 'primary' as const };
};

const getCardVariant = (error: boolean, complete: boolean) => {
  if (error) return 'error';
  if (complete) return 'success';
  return 'default';
};

const UploadCard = ({ id, location }: IUploadCardProps) => {
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<undefined | string>();
  const [complete, setComplete] = useState(false);

  const status = getStatus(!!error, complete);
  const cardVariant = getCardVariant(!!error, complete);

  const handleReceiveUpdate = useCallback(
    (_id: string, data: FileUploadPubSubPayload) => {
      if (data.type === 'progress') {
        setProgress(data.body);
        return;
      }

      if (data.type === 'error') {
        setError(data.body);
        return;
      }

      if (data.type === 'complete') {
        setComplete(true);
      }
    },
    [],
  );

  useEffect(() => {
    const subscriptionToken = PubSub.subscribe(
      `upload-${id}`,
      handleReceiveUpdate,
    );

    return () => {
      PubSub.unsubscribe(subscriptionToken);
    };
  }, [handleReceiveUpdate, id]);

  const clearUploads = () => undefined;

  return (
    <Card variant={cardVariant} sx={{ borderRadius: 2.5 }}>
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.25}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: 600, minWidth: 0 }}
            >
              {location}
            </Typography>
            <Chip label={status.label} color={status.color} size="small" />
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={status.color}
            sx={{ height: 7, borderRadius: 999 }}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              {progress.toFixed()}% uploaded
            </Typography>
            {complete && (
              <Button onClick={clearUploads} size="small" variant="text">
                Clear
              </Button>
            )}
          </Stack>
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default UploadCard;
